import { useEffect, useMemo, useRef, useState } from 'react';
import { ARCHETYPE_ORDER } from '../../data';
import { useArchetypes } from '../../i18n/archetypes';
import { useCopy, useLang } from '../../i18n/LanguageContext';
import { saveResponse } from '../../supabase';
import { IS_NATIVE } from '../../config/platform';
import { saveProfile } from '../../native/localStore';
import { tap } from '../../native/nativeShell';
import QuizAanmelding from '../../components/QuizAanmelding.jsx';
import Results from '../../components/Results.jsx';
import Onthulling from './Onthulling.jsx';
import { getQuizData } from './quizTestData';

/**
 * QuizTest — EXPERIMENTEEL (testvariant vragenlijst)
 *
 * Verkorte flow uit het analysestuk "Korter afnemen, scherper prijzen":
 *   1. Aanmelden (hergebruikt QuizAanmelding)
 *   2. 9 basisvragen met vast antwoordpatroon (5x één keuze, 4x twee in volgorde)
 *   3. Keuzescherm ZONDER profiel: verdiepen of meteen onthullen
 *   4. Optionele verdieping (2 duels, druk, werkplekgebruik, open vraag)
 *   5. Onthulling, daarna het profiel (hergebruikt Results)
 *
 * Het profiel is al berekend bij stap 3, maar komt bewust nog niet in beeld.
 * Eerder stond het er wél, en dan scrolde men eraan voorbij zonder ooit op een
 * knop te drukken — waardoor er niets werd opgeslagen en je de volgende keer
 * opnieuw mocht beginnen. Nu loopt elke weg naar je profiel via fase
 * 'profielScherp', en daar wordt het één keer bewaard: op het web via
 * saveResponse naar Supabase, in app-modus op het toestel zelf.
 */

const WEIGHTS = [3, 2]; // eerste keuze telt 3, tweede 2
const BUMP = 3; // gewicht dat een gewonnen duel / druk-antwoord toevoegt

const PALETTE = {
    bg: '#f7f2ec',
    ink: '#1f1b18',
    accent: '#b85c5c',
    soft: '#6a5f58',
    line: '#ddd6ce',
};

function shuffleArray(array) {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

// Scores hangen alleen aan de persona-ids — die zijn taal-onafhankelijk.
function emptyScores() {
    return Object.fromEntries(ARCHETYPE_ORDER.map((id) => [id, 0]));
}

// De persona-lijst komt uit de actieve taal en wordt meegegeven.
function sortByScore(archetypes, scores) {
    return [...archetypes]
        .map((a) => ({ ...a, score: scores[a.id] || 0 }))
        .sort((a, b) => b.score - a.score);
}

// ── Presentatie-componenten (module-niveau: stabiele identiteit, geen remount) ─
function Shell({ children, isMobile, innerRef }) {
    return (
        <div
            style={{
                // Op de telefoon GEEN minimumhoogte: die was op de webkop van
                // 88 px gerekend, terwijl de app een kop van 69 px en een
                // tabbalk van 56 px heeft. Samen 125 px, dus de pagina was
                // altijd een paar tientallen pixels scrollbaar — precies het
                // "wegzakken" van de vraag. Zonder minimumhoogte scrollt er
                // niets meer zodra de inhoud past.
                minHeight: isMobile ? 'auto' : 'calc(100vh - 88px)',
                display: 'flex',
                alignItems: isMobile ? 'flex-start' : 'center',
                justifyContent: 'center',
                padding: isMobile ? '8px 12px 12px' : '24px 20px',
                background: PALETTE.bg,
                boxSizing: 'border-box',
            }}
        >
            <div ref={innerRef} style={{ width: '100%', maxWidth: 960, display: 'grid', gap: isMobile ? 8 : 12 }}>
                {children}
            </div>
        </div>
    );
}

function Card({ children, isMobile }) {
    return (
        <div
            style={{
                background: 'white',
                borderRadius: 20,
                padding: isMobile ? '14px 14px' : '26px 30px 22px',
                border: `1px solid ${PALETTE.line}`,
                boxShadow: '0 12px 32px rgba(31,27,24,0.08)',
                display: 'grid',
                gap: isMobile ? 'clamp(9px, 1.4vh, 11px)' : 16,
            }}
        >
            {children}
        </div>
    );
}

function Eyebrow({ children }) {
    return (
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, color: PALETTE.accent }}>
            {children}
        </div>
    );
}

function Title({ children, isMobile }) {
    return (
        <h1
            style={{
                fontFamily: 'Playfair Display',
                // Zie de toelichting bij OptionButton: op een korte telefoon
                // krimpt ook de vraag mee, met 18 px als ondergrens.
                fontSize: isMobile ? 'clamp(18px, 2.8vh, 21px)' : 28,
                lineHeight: isMobile ? 1.22 : 1.18,
                margin: 0,
                color: PALETTE.ink,
                fontWeight: 500,
            }}
        >
            {children}
        </h1>
    );
}

function PrimaryBtn({ children, onClick, disabled, isMobile }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                background: disabled ? '#d8cec5' : PALETTE.ink,
                color: 'white',
                padding: isMobile ? '11px 20px' : '13px 22px',
                borderRadius: 12,
                border: 'none',
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: 15,
                fontWeight: 600,
                minWidth: isMobile ? '100%' : 200,
            }}
        >
            {children}
        </button>
    );
}

function GhostBtn({ children, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                background: 'transparent',
                color: PALETTE.soft,
                padding: '13px 18px',
                borderRadius: 12,
                border: `1px solid ${PALETTE.line}`,
                cursor: 'pointer',
                fontSize: 15,
                fontWeight: 600,
            }}
        >
            {children}
        </button>
    );
}

function OptionButton({ option, order, onClick, isMobile }) {
    const active = order !== null;
    return (
        <button
            type="button"
            onClick={onClick}
            style={{
                padding: isMobile ? '7px 11px' : '12px 14px',
                borderRadius: 12,
                border: active ? `2px solid ${PALETTE.accent}` : '1px solid #ddd',
                background: active ? '#fcf1f1' : 'white',
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: isMobile ? 'clamp(12.5px, 1.72vh, 13.5px)' : 15,
                lineHeight: isMobile ? 1.3 : 1.35,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                // Acht knoppen zijn het zwaarste blok op het scherm. Op de
                // telefoon krimpen ze mee met de schermhoogte: op een ruime
                // telefoon blijven ze 40 px, op een korte (iPhone SE) worden ze
                // net zoveel lager als nodig om de hele vraag zonder scrollen
                // te laten passen. De binnenmarge is de ondergrens.
                minHeight: isMobile ? 'clamp(32px, 4.6vh, 40px)' : 50,
            }}
        >
            <span style={{ paddingRight: 6 }}>{option.text}</span>
            {order !== null && (
                <span
                    style={{
                        // Op de telefoon kleiner dan de knop hoog is, anders
                        // rekt een gekozen antwoord op en schuift de rest
                        // zichtbaar naar beneden.
                        minWidth: isMobile ? 20 : 26,
                        height: isMobile ? 20 : 26,
                        borderRadius: 999,
                        background: PALETTE.accent,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                    }}
                >
                    {order}
                </span>
            )}
        </button>
    );
}

function PlekGrid({ list, setList, isMobile, types }) {
    const toggle = (id) => {
        if (list.includes(id)) {
            setList(list.filter((x) => x !== id));
            return;
        }
        if (list.length >= 3) return;
        setList([...list, id]);
    };
    return (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: 8 }}>
            {types.map((t) => {
                const active = list.includes(t.id);
                const full = list.length >= 3 && !active;
                return (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => toggle(t.id)}
                        style={{
                            padding: isMobile ? '7px 10px' : '10px 12px',
                            borderRadius: 12,
                            border: active ? `2px solid ${PALETTE.accent}` : '1px solid #ddd',
                            background: active ? '#fcf1f1' : 'white',
                            color: full ? '#b9b0a8' : PALETTE.ink,
                            cursor: full ? 'not-allowed' : 'pointer',
                            fontSize: 14,
                            lineHeight: 1.3,
                            textAlign: 'left',
                            // Twee lijsten van acht onder elkaar: krimpen mee
                            // met de schermhoogte, net als de antwoordknoppen.
                            minHeight: isMobile ? 'clamp(34px, 5.3vh, 46px)' : 46,
                        }}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
}

function ProfielBanner({ eyebrow, text, isMobile, children }) {
    return (
        <div style={{ background: PALETTE.bg, padding: isMobile ? '16px 12px 0' : '24px 20px 0' }}>
            <div style={{ maxWidth: 960, margin: '0 auto' }}>
                <div
                    style={{
                        background: 'white',
                        borderRadius: 20,
                        // Losse zijden i.p.v. de `border`-shorthand: React
                        // waarschuwt als shorthand en longhand in dezelfde
                        // render allebei worden bijgewerkt.
                        borderTop: `4px solid ${PALETTE.accent}`,
                        borderRight: `1px solid ${PALETTE.line}`,
                        borderBottom: `1px solid ${PALETTE.line}`,
                        borderLeft: `1px solid ${PALETTE.line}`,
                        boxShadow: '0 12px 32px rgba(31,27,24,0.08)',
                        padding: isMobile ? '18px 16px' : '22px 26px',
                        display: 'grid',
                        gap: 14,
                    }}
                >
                    <div>
                        <Eyebrow>{eyebrow}</Eyebrow>
                        <p style={{ margin: '6px 0 0', color: PALETTE.soft, fontSize: 15, lineHeight: 1.5 }}>{text}</p>
                    </div>
                    {children}
                </div>
            </div>
        </div>
    );
}

export default function QuizTest({ setPage }) {
    const ARCHETYPES = useArchetypes();
    const { lang } = useLang();
    const { quiz: t } = useCopy();
    const {
        BASIS_VRAGEN,
        DRUK_VRAAG,
        WERKPLEK_TYPES,
        OPEN_VRAAG,
        DUEL_ESSENTIE,
        DUEL_INTRO,
    } = useMemo(() => getQuizData(lang), [lang]);
    // intake | basis | keuze | verdieping | onthulling | profielScherp
    const [phase, setPhase] = useState('intake');
    const [profile, setProfile] = useState(null);
    const [scores, setScores] = useState(emptyScores());

    // basis
    const [basisStep, setBasisStep] = useState(0);
    const [shuffled, setShuffled] = useState([]);
    const [selected, setSelected] = useState([]);
    const [error, setError] = useState('');

    // verdieping
    const [verdiepStep, setVerdiepStep] = useState(0); // 0=duel1 1=duel2 2=druk 3=werkplek 4=open
    // Of de verdieping gelopen is — bepaalt welke tekst er bij het profiel staat.
    const [verdiept, setVerdiept] = useState(false);
    const [duelBumps, setDuelBumps] = useState({});
    const [drukShuffled, setDrukShuffled] = useState([]);
    const [drukChoice, setDrukChoice] = useState(null);
    const [gebruikNu, setGebruikNu] = useState([]);
    const [mistPlek, setMistPlek] = useState([]);
    const [openText, setOpenText] = useState('');

    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 768 : false
    );
    const topRef = useRef(null);

    // Opslag — bij afronden precies één keer naar Supabase.
    const savedRef = useRef(false);
    const [saveError, setSaveError] = useState('');
    // App-modus: het bewaarde profiel op het toestel, zodat je er meteen een
    // aantekening bij kunt maken.
    const [bewaardProfiel, setBewaardProfiel] = useState(null);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const currentBasis = phase === 'basis' ? BASIS_VRAGEN[basisStep] : null;

    // Shuffle opties bij elke basisvraag.
    useEffect(() => {
        if (phase !== 'basis' || !currentBasis) return;
        const opts = currentBasis.a.map((text, index) => ({
            text,
            archetypeId: ARCHETYPE_ORDER[index],
        }));
        setShuffled(shuffleArray(opts));
        setSelected([]);
        setError('');
        if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [phase, basisStep]); // eslint-disable-line react-hooks/exhaustive-deps

    // Shuffle druk-opties zodra we bij die stap zijn.
    useEffect(() => {
        if (phase !== 'verdieping' || verdiepStep !== 2) return;
        const opts = DRUK_VRAAG.a.map((text, index) => ({
            text,
            archetypeId: ARCHETYPE_ORDER[index],
        }));
        setDrukShuffled(shuffleArray(opts));
        // DRUK_VRAAG bewust niet in deps: getQuizData() geeft per render een nieuw
        // object, dat zou de opties elke render opnieuw husselen.
    }, [phase, verdiepStep]); // eslint-disable-line react-hooks/exhaustive-deps

    const buildResult = (finalScores) => {
        const sorted = sortByScore(ARCHETYPES, finalScores);
        return {
            ...profile,
            primary: sorted[0]?.id || null,
            secondary: sorted[1]?.id || null,
            tertiary: sorted[2]?.id || null,
            scores: finalScores,
        };
    };

    const verscherpteScores = useMemo(() => {
        const s = { ...scores };
        Object.entries(duelBumps).forEach(([id, v]) => {
            s[id] = (s[id] || 0) + v;
        });
        if (drukChoice?.archetypeId) {
            s[drukChoice.archetypeId] = (s[drukChoice.archetypeId] || 0) + BUMP;
        }
        return s;
    }, [scores, duelBumps, drukChoice]);

    const basisResult = useMemo(
        () => (profile ? buildResult(scores) : null),
        [profile, scores] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const scherpResult = useMemo(
        () =>
            profile
                ? { ...buildResult(verscherpteScores), werkplek: { gebruikNu, mist: mistPlek }, openAntwoord: openText }
                : null,
        [profile, verscherpteScores, gebruikNu, mistPlek, openText] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Opslaan zodra het verscherpte profiel in beeld komt — één keer.
    // Beide paden ('Nee, ik ben klaar' en de afgeronde verdieping) landen hier.
    // In app-modus gaat er niets naar een server: het profiel wordt op het
    // toestel bewaard en verschijnt daar in je historie.
    useEffect(() => {
        if (phase !== 'profielScherp' || savedRef.current || !scherpResult) return;
        if (IS_NATIVE) {
            savedRef.current = true;
            setBewaardProfiel(saveProfile(scherpResult));
            return;
        }
        savedRef.current = true;
        setSaveError('');
        (async () => {
            try {
                await saveResponse(scherpResult);
            } catch (err) {
                savedRef.current = false; // mislukt → volgende render mag opnieuw
                setSaveError(err?.message || t.errors.saveFailed);
            }
        })();
        // Alleen fase en resultaat sturen het opslaan aan; `t` is puur fouttekst.
    }, [phase, scherpResult]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortedBasis = useMemo(() => sortByScore(ARCHETYPES, scores), [ARCHETYPES, scores]);
    const duelPairs = useMemo(
        () => [
            [sortedBasis[0], sortedBasis[1]],
            [sortedBasis[1], sortedBasis[2]],
        ],
        [sortedBasis]
    );

    // ── Handlers ────────────────────────────────────────────────────────────────
    const toggleSelect = (option) => {
        // Korte tik bij het kiezen van een antwoord, niet bij het weghalen.
        // Buiten de updater, want die mag geen bijwerkingen hebben.
        const alGekozen = selected.some((s) => s.text === option.text);
        if (!alGekozen && selected.length < currentBasis.pick) tap();
        setSelected((prev) => {
            const exists = prev.some((s) => s.text === option.text);
            if (exists) return prev.filter((s) => s.text !== option.text);
            if (prev.length >= currentBasis.pick) return prev;
            return [...prev, option];
        });
    };

    const nextBasis = () => {
        if (selected.length !== currentBasis.pick) {
            setError(currentBasis.pick === 2 ? t.errors.pickTwo : t.errors.pickOne);
            return;
        }
        const next = { ...scores };
        selected.forEach((opt, i) => {
            if (opt.archetypeId) next[opt.archetypeId] = (next[opt.archetypeId] || 0) + WEIGHTS[i];
        });
        setScores(next);
        if (basisStep + 1 < BASIS_VRAGEN.length) setBasisStep((s) => s + 1);
        else setPhase('keuze');
    };

    const kiesDuel = (archetypeId) => {
        tap();
        setDuelBumps((prev) => ({ ...prev, [archetypeId]: (prev[archetypeId] || 0) + BUMP }));
        setVerdiepStep((s) => (s === 0 ? 1 : 2));
    };

    // ── INTAKE ────────────────────────────────────────────────────────────────
    if (phase === 'intake') {
        return (
            <QuizAanmelding
                onSubmit={(p) => {
                    setProfile(p);
                    setScores(emptyScores());
                    setBasisStep(0);
                    setPhase('basis');
                }}
            />
        );
    }

    // ── BASIS ────────────────────────────────────────────────────────────────────
    if (phase === 'basis') {
        const progress = ((basisStep + 1) / BASIS_VRAGEN.length) * 100;
        const hint = currentBasis.pick === 2 ? t.pickTwo : t.pickOne;
        const hintTekst = (
            <>
                {hint.before}
                <strong style={{ color: PALETTE.ink }}>{hint.strong}</strong>
                {hint.after}
            </>
        );
        return (
            <Shell isMobile={isMobile} innerRef={topRef}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1, height: 5, background: '#e6dfd8', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${progress}%`, height: '100%', background: PALETTE.accent, borderRadius: 999, transition: 'width .3s' }} />
                    </div>
                    <span style={{ color: 'var(--tof-text-muted)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1.5, whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {basisStep + 1} / {BASIS_VRAGEN.length}
                    </span>
                </div>

                <Card isMobile={isMobile}>
                    {/*
                      Op de telefoon staat de instructie als kale regel ónder de
                      vraag, zonder balk en zonder de "9 vragen · ± 5 minuten"
                      erboven: die herhaalt alleen wat de voortgangsbalk al
                      zegt, en samen kostten ze de ruimte waardoor de vraag
                      buiten beeld zakte.
                    */}
                    <div>
                        {!isMobile && <Eyebrow>{t.eyebrow(BASIS_VRAGEN.length)}</Eyebrow>}
                        <Title isMobile={isMobile}>{currentBasis.q}</Title>
                        {isMobile && (
                            <p style={{ margin: '5px 0 0', fontSize: 12.5, lineHeight: 1.4, color: PALETTE.soft }}>
                                {hintTekst}
                            </p>
                        )}
                    </div>

                    {!isMobile && (
                        <div style={{ background: PALETTE.bg, borderRadius: 10, padding: '9px 14px', fontSize: 13, color: PALETTE.soft, lineHeight: 1.4 }}>
                            {hintTekst}
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? 6 : 8 }}>
                        {shuffled.map((option, index) => {
                            const idx = selected.findIndex((s) => s.text === option.text);
                            const order = idx === -1 ? null : currentBasis.pick === 1 ? '✓' : idx + 1;
                            return (
                                <OptionButton
                                    key={`${option.text}-${index}`}
                                    option={option}
                                    order={order}
                                    isMobile={isMobile}
                                    onClick={() => toggleSelect(option)}
                                />
                            );
                        })}
                    </div>

                    {error && <div style={{ color: PALETTE.accent, fontSize: 14 }}>{error}</div>}

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <PrimaryBtn isMobile={isMobile} onClick={nextBasis} disabled={selected.length !== currentBasis.pick}>
                            {basisStep + 1 === BASIS_VRAGEN.length ? t.toProfile : t.nextQuestion}
                        </PrimaryBtn>
                    </div>
                </Card>
            </Shell>
        );
    }

    // ── KEUZE — het profiel is berekend maar nog niet te zien ───────────────────
    if (phase === 'keuze') {
        // Liggen de nummers 1 en 2 binnen een enkele vraag van elkaar? Dan is
        // de volgorde eigenlijk nog niet beslist en duwen we naar de verdieping.
        const nek = (sortedBasis[0]?.score || 0) - (sortedBasis[1]?.score || 0) <= WEIGHTS[1];
        const verdiepen = () => { setVerdiept(true); setVerdiepStep(0); setPhase('verdieping'); };
        const onthullen = () => setPhase('onthulling');
        return (
            <Shell isMobile={isMobile} innerRef={topRef}>
                <Card isMobile={isMobile}>
                    <div>
                        <Eyebrow>{t.profileReady.eyebrow}</Eyebrow>
                        <Title isMobile={isMobile}>{nek ? t.profileReady.closeTitle : t.profileReady.clearTitle}</Title>
                        <p style={{ margin: '8px 0 0', color: PALETTE.soft, fontSize: 15, lineHeight: 1.55 }}>
                            {nek ? t.profileReady.closeBody : t.profileReady.clearBody}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {nek ? (
                            <>
                                <PrimaryBtn isMobile={isMobile} onClick={verdiepen}>{t.profileReady.refine}</PrimaryBtn>
                                <GhostBtn onClick={onthullen}>{t.profileReady.done}</GhostBtn>
                            </>
                        ) : (
                            <>
                                <PrimaryBtn isMobile={isMobile} onClick={onthullen}>{t.profileReady.done}</PrimaryBtn>
                                <GhostBtn onClick={verdiepen}>{t.profileReady.refine}</GhostBtn>
                            </>
                        )}
                    </div>
                    <p style={{ margin: 0, color: '#a89e96', fontSize: 13 }}>{t.profileReady.saveHint}</p>
                </Card>
            </Shell>
        );
    }

    // ── ONTHULLING ──────────────────────────────────────────────────────────────
    if (phase === 'onthulling') {
        return (
            <Onthulling
                archetypes={ARCHETYPES}
                primaryId={scherpResult?.primary}
                isMobile={isMobile}
                eyebrow={t.reveal.eyebrow}
                onDone={() => setPhase('profielScherp')}
            />
        );
    }

    // ── VERDIEPING ───────────────────────────────────────────────────────────────
    if (phase === 'verdieping') {
        if (verdiepStep === 0 || verdiepStep === 1) {
            const pair = duelPairs[verdiepStep] || [];
            const [a, b] = pair;
            if (!a || !b) {
                setVerdiepStep(2);
                return null;
            }
            return (
                <Shell isMobile={isMobile} innerRef={topRef}>
                    <Card isMobile={isMobile}>
                        <div>
                            <Eyebrow>{t.deepDive.duelEyebrow(verdiepStep + 1, 2)}</Eyebrow>
                            <Title isMobile={isMobile}>{DUEL_INTRO}</Title>
                        </div>
                        <div style={{ display: 'grid', gap: 10 }}>
                            {[a, b].map((arch) => (
                                <OptionButton
                                    key={arch.id}
                                    option={{ text: DUEL_ESSENTIE[arch.id] || arch.name }}
                                    order={null}
                                    isMobile={isMobile}
                                    onClick={() => kiesDuel(arch.id)}
                                />
                            ))}
                        </div>
                    </Card>
                </Shell>
            );
        }

        if (verdiepStep === 2) {
            return (
                <Shell isMobile={isMobile} innerRef={topRef}>
                    <Card isMobile={isMobile}>
                        <div>
                            <Eyebrow>{t.deepDive.pressureEyebrow}</Eyebrow>
                            <Title isMobile={isMobile}>{DRUK_VRAAG.q}</Title>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8 }}>
                            {drukShuffled.map((option, index) => (
                                <OptionButton
                                    key={`${option.text}-${index}`}
                                    option={option}
                                    order={drukChoice?.text === option.text ? '✓' : null}
                                    isMobile={isMobile}
                                    onClick={() => setDrukChoice(option)}
                                />
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <PrimaryBtn isMobile={isMobile} onClick={() => setVerdiepStep(3)} disabled={!drukChoice}>{t.next}</PrimaryBtn>
                        </div>
                    </Card>
                </Shell>
            );
        }

        if (verdiepStep === 3) {
            return (
                <Shell isMobile={isMobile} innerRef={topRef}>
                    <Card isMobile={isMobile}>
                        <div>
                            <Eyebrow>{t.deepDive.workplaceEyebrow}</Eyebrow>
                            <Title isMobile={isMobile}>{t.deepDive.workplaceNowTitle}</Title>
                            <p style={{ margin: '4px 0 0', color: PALETTE.soft, fontSize: 13 }}>{t.deepDive.chooseThree}</p>
                        </div>
                        <PlekGrid list={gebruikNu} setList={setGebruikNu} isMobile={isMobile} types={WERKPLEK_TYPES} />
                        <div style={{ borderTop: `1px solid ${PALETTE.line}`, paddingTop: 16 }}>
                            <Title isMobile={isMobile}>{t.deepDive.workplaceMissTitle}</Title>
                            <p style={{ margin: '4px 0 10px', color: PALETTE.soft, fontSize: 13 }}>{t.deepDive.chooseThree}</p>
                            <PlekGrid list={mistPlek} setList={setMistPlek} isMobile={isMobile} types={WERKPLEK_TYPES} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <PrimaryBtn
                                isMobile={isMobile}
                                onClick={() => setVerdiepStep(4)}
                                disabled={gebruikNu.length !== 3 || mistPlek.length !== 3}
                            >
                                {t.next}
                            </PrimaryBtn>
                        </div>
                    </Card>
                </Shell>
            );
        }

        return (
            <Shell isMobile={isMobile} innerRef={topRef}>
                <Card isMobile={isMobile}>
                    <div>
                        <Eyebrow>{t.deepDive.openEyebrow}</Eyebrow>
                        <Title isMobile={isMobile}>{OPEN_VRAAG.q}</Title>
                        <p style={{ margin: '4px 0 0', color: PALETTE.soft, fontSize: 13 }}>{t.deepDive.openHint(OPEN_VRAAG.maxLength)}</p>
                    </div>
                    <textarea
                        value={openText}
                        maxLength={OPEN_VRAAG.maxLength}
                        onChange={(e) => setOpenText(e.target.value)}
                        style={{
                            width: '100%',
                            minHeight: 96,
                            padding: '12px 14px',
                            border: '1px solid #ddd',
                            borderRadius: 12,
                            fontSize: 15,
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            boxSizing: 'border-box',
                        }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 12, color: '#a89e96' }}>{openText.length}/{OPEN_VRAAG.maxLength}</span>
                        <PrimaryBtn isMobile={isMobile} onClick={() => setPhase('onthulling')}>{t.deepDive.showSharper}</PrimaryBtn>
                    </div>
                </Card>
            </Shell>
        );
    }

    // ── PROFIEL ──────────────────────────────────────────────────────────────────
    if (phase === 'profielScherp') {
        const veranderd = basisResult?.primary !== scherpResult?.primary;
        return (
            <div>
                <ProfielBanner
                    isMobile={isMobile}
                    eyebrow={verdiept ? t.sharpened.eyebrow : t.sharpened.basisEyebrow}
                    text={
                        verdiept
                            ? (veranderd ? t.sharpened.changed : t.sharpened.confirmed)
                            : t.sharpened.basis
                    }
                >
                    {saveError && (
                        <p style={{ margin: 0, color: PALETTE.accent, fontSize: 14 }}>{saveError}</p>
                    )}
                </ProfielBanner>
                <Results resultData={scherpResult} setPage={setPage} noteEntry={bewaardProfiel} />
            </div>
        );
    }

    return null;
}
